import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { WorkspacesService } from '../../services/WorkspacesService';
import { Environment, ProvisionTask, ProvisionSubTask, PageResponse } from './types';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    maxHeight: 500,
    overflow: 'auto',
  },
  headerCell: {
    backgroundColor: '#a0a0a0',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
  },
  taskRow: {
    backgroundColor: '#f5f5f5',
  },
  subtaskRow: {
    backgroundColor: '#fafafa',
  },
  expandCell: {
    width: 50,
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
}));

interface TasksDialogProps {
  open: boolean;
  onClose: () => void;
  provisionId: string | null;
  environment: Environment;
  workspacesService: WorkspacesService;
}

const INITIAL_PAGE: PageResponse<ProvisionTask> = {
  items: [],
  hasNext: false,
};

const STATUS_COLORS = {
  PENDING: '#9E9E9E',
  RUNNING: '#3F51B5',
  SUCCEEDED: '#4CAF50',
  FAILED: '#E53935',
} as const;

const TABLE_COLUMNS_COUNT = 4;

export const TasksDialog: React.FC<TasksDialogProps> = ({
                                                          open,
                                                          onClose,
                                                          provisionId,
                                                          environment,
                                                          workspacesService,
                                                        }) => {
  const classes = useStyles();
  const [tasksPage, setTasksPage] = useState<PageResponse<ProvisionTask>>(INITIAL_PAGE);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [subTasksByTask, setSubTasksByTask] = useState<
    Record<string, ProvisionSubTask[]>
  >({});
  const [subTasksLoading, setSubTasksLoading] = useState<
    Record<string, boolean>
  >({});

  const getStatusColor = useCallback((status: string): string => {
    const normalizedStatus = status?.toUpperCase() as keyof typeof STATUS_COLORS;
    return STATUS_COLORS[normalizedStatus] || STATUS_COLORS.PENDING;
  }, []);

  const loadTasks = useCallback(async () => {
    if (!provisionId) {
      console.warn('Provision ID not provided');
      return;
    }

    setTasksLoading(true);

    try {
      const response = await workspacesService.getProvisionTasks(
        environment,
        provisionId,
      );

      setTasksPage({
        ...response,
        items: response?.items || [],
      });
    } catch (error) {
      console.error('Error fetching provision tasks:', error);
      setTasksPage(INITIAL_PAGE);
    } finally {
      setTasksLoading(false);
    }
  }, [provisionId, environment, workspacesService]);

  const loadSubTasks = useCallback(
    async (taskId: string) => {
      setSubTasksLoading(prev => ({ ...prev, [taskId]: true }));

      try {
        const response = await workspacesService.getProvisionSubTasks(
          environment,
          taskId,
        );

        setSubTasksByTask(prev => ({
          ...prev,
          [taskId]: response?.items || [],
        }));
      } catch (error) {
        console.error('Error fetching subtasks:', error);
        setSubTasksByTask(prev => ({ ...prev, [taskId]: [] }));
      } finally {
        setSubTasksLoading(prev => ({ ...prev, [taskId]: false }));
      }
    },
    [environment, workspacesService],
  );

  const toggleTask = useCallback(
    async (task: ProvisionTask) => {
      const taskId = task.id;
      const updatedExpanded = new Set(expandedTasks);

      if (updatedExpanded.has(taskId)) {
        updatedExpanded.delete(taskId);
      } else {
        updatedExpanded.add(taskId);

        if (!subTasksByTask[taskId]) {
          await loadSubTasks(taskId);
        }
      }

      setExpandedTasks(updatedExpanded);
    },
    [expandedTasks, subTasksByTask, loadSubTasks],
  );

  const hasTasks = useMemo(
    () => !tasksLoading && tasksPage.items.length > 0,
    [tasksLoading, tasksPage.items.length],
  );

  const getSubTasksLength = useCallback(
    (taskId: string) => subTasksByTask[taskId]?.length ?? 0,
    [subTasksByTask],
  );

  const isSubTaskLoading = useCallback(
    (taskId: string) => subTasksLoading[taskId] ?? false,
    [subTasksLoading],
  );

  useEffect(() => {
    if (open && provisionId) {
      setExpandedTasks(new Set());
      setSubTasksByTask({});
      setSubTasksLoading({});
      loadTasks();
    }
  }, [open, provisionId, loadTasks]);

  const renderSubTasksSection = useCallback(
    (taskId: string) => {
      if (isSubTaskLoading(taskId)) {
        return (
          <TableRow>
            <TableCell colSpan={TABLE_COLUMNS_COUNT} align="center">
              <CircularProgress size={20} />
              <Typography variant="caption">Loading subtasks...</Typography>
            </TableCell>
          </TableRow>
        );
      }

      const subTasksLength = getSubTasksLength(taskId);

      if (subTasksLength === 0) {
        return (
          <TableRow>
            <TableCell colSpan={TABLE_COLUMNS_COUNT}>
              <Box pl={8} py={1}>
                <Typography variant="body2" color="textSecondary">
                  No subtask found for this task.
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        );
      }

      return subTasksByTask[taskId].map(subtask => (
        <TableRow key={subtask.subTaskId} className={classes.subtaskRow}>
          <TableCell />
          <TableCell>
            <Box pl={4}>{subtask.component || 'N/A'}</Box>
          </TableCell>
          <TableCell>
            <Chip
              label={subtask.status || 'Unknown'}
              className={classes.statusChip}
              style={{
                backgroundColor: getStatusColor(subtask.status || ''),
              }}
              size="small"
            />
          </TableCell>
          <TableCell>{subtask.error || ''}</TableCell>
        </TableRow>
      ));
    },
    [
      classes,
      subTasksByTask,
      isSubTaskLoading,
      getSubTasksLength,
      getStatusColor,
    ],
  );

  const renderTaskRow = useCallback(
    (task: ProvisionTask) => (
      <React.Fragment key={task.id}>
        <TableRow className={classes.taskRow}>
          <TableCell className={classes.expandCell}>
            <IconButton
              size="small"
              onClick={() => toggleTask(task)}
              disabled={isSubTaskLoading(task.id)}
            >
              {expandedTasks.has(task.id) ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
          <TableCell>{task.provisionTaskType || 'N/A'}</TableCell>
          <TableCell>
            <Chip
              label={task.provisionTaskStatus || 'Unknown'}
              className={classes.statusChip}
              style={{
                backgroundColor: getStatusColor(task.provisionTaskStatus || ''),
              }}
              size="small"
            />
          </TableCell>
          <TableCell>{task.systemIds || 'N/A'}</TableCell>
        </TableRow>

        {expandedTasks.has(task.id) && renderSubTasksSection(task.id)}
      </React.Fragment>
    ),
    [
      classes,
      expandedTasks,
      toggleTask,
      isSubTaskLoading,
      getStatusColor,
      renderSubTasksSection,
    ],
  );

  const renderTableBody = useCallback(() => {
    if (tasksLoading) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_COLUMNS_COUNT} align="center">
            <CircularProgress size={24} />
            <Typography>Loading tasks...</Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (!hasTasks) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_COLUMNS_COUNT} align="center">
            <Typography variant="body2" color="textSecondary">
              No task found for this provision.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return tasksPage.items.map(renderTaskRow);
  }, [tasksLoading, hasTasks, tasksPage.items, renderTaskRow]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Provisioning Tasks</DialogTitle>
      <DialogContent>
        <Box className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.headerCell} ${classes.expandCell}`}
                />
                <TableCell className={classes.headerCell}>Name</TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell}>System</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
